<template>
  <div class="px-5 py-[44px]">
    <div
      ref="columnRoot"
      class="grid min-w-[200px] grid-cols-[repeat(auto-fit,minmax(150px,max(150px,100%/6)))] gap-3"
      :class="{
        'max-w-[400px]': hasNoChildrenAtAll || hide3rdLevel,
        'max-w-[840px]': !hasNoChildrenAtAll && !hide3rdLevel,
      }"
    >
      <template v-for="category in categoryChildren">
        <div
          v-if="category && !isHiddenForMainNavigation(category)"
          :key="category.slug"
          class="w-36"
        >
          <div
            v-if="
              hide3rdLevel ||
              (category.children && category.children.length === 0)
            "
          >
            <AgsLink
              :to="linkCategoryTo(category)"
              class="mb-[10px] font-bold normal-case leading-[19px]"
              size="base"
              data-qa="sub-category"
              data-test-id="menu-sub-category-link"
              :data-qa-sub-category="category.name"
              @click="onClick(category, $event as any)"
            >
              {{ category.name }}
            </AgsLink>
          </div>

          <div v-else>
            <AgsLink
              :to="linkCategoryTo(category)"
              class="mb-[10px] font-bold normal-case leading-[19px]"
              size="base"
              data-qa="sub-category"
              data-test-id="menu-sub-category-link"
              :data-qa-sub-category="category.name"
              @click="onClick(category, $event)"
            >
              {{ category.name }}
            </AgsLink>
            <OneColumnList
              :items="category.children"
              :path="linkCategoryTo(category)"
              :filter="isVisible"
            >
              <template #item="{ item }">
                <AgsLink
                  :to="linkCategoryTo(item)"
                  :slice="itemsLength"
                  badge-placement="top"
                  badge-size="sm"
                  size="sm"
                  class="mb-[6px] font-normal normal-case leading-[19px]"
                  data-qa="sub-category"
                  :data-qa-sub-category="item.name"
                  @click="onClick(item, $event)"
                >
                  {{ item.name }}
                </AgsLink>
              </template>
            </OneColumnList>
            <AgsLink
              v-if="
                category.children &&
                category.children.filter(
                  (item) => !isHiddenForMainNavigation(item),
                ).length > itemsLength
              "
              :to="linkCategoryTo(category)"
              :aria-label="`${$t('header.more')} ${category.name}`"
              badge-placement="top"
              badge-size="sm"
              size="sm"
              class="mb-[6px] font-bold normal-case leading-[19px]"
              @click="onClick(category, $event)"
            >
              {{ $t('header.more') }}
              <span class="sr-only"> {{ category.name }}</span>
            </AgsLink>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import type { Category } from '@scayle/storefront-nuxt'
import {
  isHiddenForMainNavigation,
  linkCategoryTo,
} from '#layers/base/utils/category'
import useUiState from '~/composables/ui/useUiState'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'

const { disableTracking } = defineProps({
  itemsLength: {
    type: Number,
    default: 4,
    validator: (propValue: number) => [3, 4, 5, 6, 7].includes(propValue),
  },
  hasNoChildrenAtAll: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  hide3rdLevel: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  categoryChildren: {
    type: Array as PropType<Category[]>,
    default: () => [],
  },
  disableTracking: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['register-column'])
const { trackNavigationFlyout } = useTrackingEvents()
const { closeFlyoutMenu } = useUiState()
const columnRoot = ref(null)

const onClick = (category: Category, event: MouseEvent) => {
  if (!disableTracking) {
    trackNavigationFlyout(category.name, category.depth)
  }

  closeFlyoutMenu(event, true)
}

const isVisible = (item: Category) => !isHiddenForMainNavigation(item)

onMounted(() => {
  emit('register-column', columnRoot.value)
})
</script>
